import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trash2, Users, Search, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useFriends,
  useUnshareGoal,
  useUnshareTask,
  useUnshareNote,
  useUnshareCalendarEvent,
  useGoalShares,
  useTaskShares,
  useNoteShares,
  useCalendarEventShares
} from "@/lib/queries/community";
import {
  useSentResourceGrants,
  useCreateResourceGrant,
  useUpdateResourceGrant,
  useRevokeResourceGrant
} from "@/lib/queries/resource-shares";

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  itemType: string;
  itemTitle: string;
}

type AssistantRelationship = {
  id: string;
  status: string;
  assistantType?: string | null;
  user: {
    id: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
};

const EDITABLE_RESOURCE_TYPES = new Set([
  "goal",
  "food_goal",
  "fitness_goal",
  "task",
  "checklist",
  "checklist_template",
]);

export function supportsResourceGrantEditing(resourceType: string): boolean {
  return EDITABLE_RESOURCE_TYPES.has(resourceType);
}

function matchesSearch(searchTerm: string, firstName: string, lastName: string, email: string): boolean {
  if (!searchTerm.trim()) return true;

  const search = searchTerm.toLowerCase().trim();
  const first = (firstName || '').toLowerCase();
  const last = (lastName || '').toLowerCase();
  const fullName = `${first} ${last}`;
  const combinedName = `${first}${last}`;
  const emailLower = (email || '').toLowerCase();

  if (first.includes(search) || last.includes(search) || fullName.includes(search) || emailLower.includes(search)) {
    return true;
  }

  if (combinedName.includes(search)) {
    return true;
  }

  return combinedName.includes(search);
}

export default function ShareDialog({ isOpen, onClose, itemId, itemType, itemTitle }: ShareDialogProps) {
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [permission, setPermission] = useState<'view' | 'edit' | 'edit_delete'>('view');
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { toast } = useToast();
  const supportsEditing = supportsResourceGrantEditing(itemType);
  const supportsDeletionRequests = itemType !== "task";

  useEffect(() => {
    if (!supportsEditing && permission !== "view") setPermission("view");
  }, [permission, supportsEditing]);

  const { data: friends = [], isLoading: loadingFriends } = useFriends();
  const { data: assistants = [], isLoading: loadingAssistants } = useQuery<AssistantRelationship[]>({
    queryKey: ["/api/assistants"],
  });

  // Fetch legacy shares
  const { data: legacyGoalShares = [], isLoading: loadingGoal } = useGoalShares(itemId, { enabled: itemType === 'goal' && isOpen });
  const { data: legacyTaskShares = [], isLoading: loadingTask } = useTaskShares(itemId, { enabled: itemType === 'task' && isOpen });
  const { data: legacyCalendarShares = [], isLoading: loadingCalendar } = useCalendarEventShares(itemId, { enabled: itemType === 'calendar_event' && isOpen });
  const { data: legacyNoteShares = [], isLoading: loadingNote } = useNoteShares(itemId, { enabled: itemType === 'note' && isOpen });

  const legacyShares = useMemo(() => {
    if (itemType === 'goal') return legacyGoalShares;
    if (itemType === 'task') return legacyTaskShares;
    if (itemType === 'calendar_event') return legacyCalendarShares;
    if (itemType === 'note') return legacyNoteShares;
    return [];
  }, [itemType, legacyGoalShares, legacyTaskShares, legacyCalendarShares, legacyNoteShares]);

  const loadingLegacy = itemType === 'goal' ? loadingGoal :
                        itemType === 'task' ? loadingTask :
                        itemType === 'calendar_event' ? loadingCalendar :
                        itemType === 'note' ? loadingNote : false;

  const { data: sentGrants = [], isLoading: loadingGrants } = useSentResourceGrants();

  const genericShares = useMemo(() => {
    return sentGrants.filter(g => g.resourceType === itemType && g.resourceId === itemId);
  }, [sentGrants, itemType, itemId]);

  const isLoading = loadingFriends || loadingAssistants || loadingLegacy || loadingGrants;

  type UnifiedShare = {
    id: string;
    isLegacy: boolean;
    recipientId: string;
    recipientName: string;
    recipientEmail: string;
    permission: string;
  };

  const combinedShares: UnifiedShare[] = useMemo(() => {
    const list: UnifiedShare[] = [];
    legacyShares.forEach((s: any) => {
      list.push({
        id: s.id,
        isLegacy: true,
        recipientId: s.sharedWithUserId || s.sharedWithId,
        recipientName: `${s.owner?.firstName || ''} ${s.owner?.lastName || ''}`.trim() || 'Unknown',
        recipientEmail: s.owner?.email || '',
        permission: s.permission || 'view'
      });
    });
    genericShares.forEach((g) => {
      list.push({
        id: g.id,
        isLegacy: false,
        recipientId: '',
        recipientName: g.displayName || 'Unknown',
        recipientEmail: '',
        permission: g.permission
      });
    });
    return list;
  }, [legacyShares, genericShares]);

  type ShareableContact = {
    id: string;
    personId: string;
    type: 'friend' | 'coach' | 'assistant';
    firstName: string;
    lastName: string;
    email: string;
  };

  const allContacts = useMemo<ShareableContact[]>(() => {
    const friendContacts = friends
      .filter(f => f.status === "active")
      .map(f => ({
      id: f.id,
      personId: f.friendId,
      type: 'friend' as const,
      firstName: f.friend.firstName || '',
      lastName: f.friend.lastName || '',
        email: f.friend.email || ''
      }));
    const assistantContacts = assistants
      .filter(assistant => assistant.status === "active" && Boolean(assistant.user?.id))
      .map(assistant => ({
        id: assistant.id,
        personId: assistant.user.id,
        type: assistant.assistantType === "coach" ? "coach" as const : "assistant" as const,
        firstName: assistant.user.firstName || "",
        lastName: assistant.user.lastName || "",
        email: assistant.user.email || "",
      }));
    return [...friendContacts, ...assistantContacts].filter(
      (contact, index, contacts) => contacts.findIndex(({ personId }) => personId === contact.personId) === index,
    );
  }, [friends, assistants]);

  const availableContacts = allContacts.filter(
    contact => !combinedShares.some(share => share.recipientId === contact.personId)
  );

  const filteredContacts = useMemo(() => {
    return availableContacts.filter(contact =>
      matchesSearch(searchQuery, contact.firstName, contact.lastName, contact.email)
    );
  }, [availableContacts, searchQuery]);

  const createGrantMutation = useCreateResourceGrant();
  const updateGrantMutation = useUpdateResourceGrant();
  const revokeGrantMutation = useRevokeResourceGrant();

  const unshareGoalMutation = useUnshareGoal();
  const unshareTaskMutation = useUnshareTask();
  const unshareNoteMutation = useUnshareNote();
  const unshareCalendarEventMutation = useUnshareCalendarEvent();

  const handleShare = async () => {
    if (!selectedPersonId) return;

    try {
      await createGrantMutation.mutateAsync({
        recipientUserId: selectedPersonId,
        resourceType: itemType,
        resourceId: itemId,
        permission: permission
      });
      setSelectedPersonId("");
      setPermission('view');
      toast({
        title: "Shared successfully",
        description: `"${itemTitle}" has been shared.`,
      });
    } catch (error) {
      toast({
        title: "Failed to share",
        description: error instanceof Error ? error.message : "An unexpected error occurred while sharing.",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePermission = async (shareId: string, newPermission: string, isLegacy: boolean) => {
    if (isLegacy) {
      toast({
        title: "Cannot update legacy share",
        description: "Please remove the share and share again to update permissions.",
        variant: "destructive"
      });
      return;
    }
    try {
      await updateGrantMutation.mutateAsync({ id: shareId, permission: newPermission });
      toast({ title: "Permission updated" });
    } catch (error) {
      toast({
        title: "Failed to update permission",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleRevoke = async (shareId: string, isLegacy: boolean) => {
    try {
      if (isLegacy) {
        if (itemType === 'goal') await unshareGoalMutation.mutateAsync(shareId);
        else if (itemType === 'task') await unshareTaskMutation.mutateAsync(shareId);
        else if (itemType === 'calendar_event') await unshareCalendarEventMutation.mutateAsync(shareId);
        else if (itemType === 'note') await unshareNoteMutation.mutateAsync(shareId);
      } else {
        await revokeGrantMutation.mutateAsync(shareId);
      }
      toast({
        title: "Share removed",
        description: "Access has been revoked.",
      });
    } catch (error) {
      toast({
        title: "Failed to remove share",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedPersonId("");
    setPermission('view');
    onClose();
  };

  const getDisplayType = () => {
    return itemType.charAt(0).toUpperCase() + itemType.slice(1).replace('_', ' ');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto sm:h-auto sm:w-full sm:max-w-md sm:rounded-lg sm:p-6" data-testid="dialog-share">
        <DialogHeader>
          <DialogTitle data-testid="text-share-title">
            Share {getDisplayType()}
          </DialogTitle>
          <DialogDescription data-testid="text-share-description">
            Share "{itemTitle}" with a person in one of your relationships
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="person-select">Select Person</Label>
              {isLoading ? (
                <div className="text-sm text-muted-foreground">Loading contacts...</div>
              ) : availableContacts.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  {allContacts.length === 0
                    ? "You don't have any active relationships yet"
                    : "Everyone in your active relationships already has access"}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search people by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="input-search-people"
                    />
                  </div>
                  {filteredContacts.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-2">
                      No people match your search
                    </div>
                  ) : (
                    <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                      <SelectTrigger id="person-select" data-testid="select-person">
                        <SelectValue placeholder="Choose a person" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredContacts.map((contact) => (
                          <SelectItem key={`${contact.type}-${contact.personId}`} value={contact.personId}>
                            <div className="flex items-center gap-2">
                              <span>{contact.firstName} {contact.lastName}</span>
                              <Badge variant="outline" className="text-[10px]">{contact.type === "coach" ? "Coach" : contact.type === "assistant" ? "Assistant" : "Friend"}</Badge>
                              <span className="text-muted-foreground text-xs">
                                ({contact.email})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="permission-select">Permission Level</Label>
              <Select value={permission} onValueChange={(value: 'view' | 'edit' | 'edit_delete') => setPermission(value)}>
                <SelectTrigger id="permission-select" data-testid="select-permission">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View</SelectItem>
                  {supportsEditing && <SelectItem value="edit">View and edit</SelectItem>}
                  {supportsEditing && supportsDeletionRequests && <SelectItem value="edit_delete">View/edit/request owner-approved deletion</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">
                {supportsEditing
                  ? "Recipients can never delete immediately. Deletion requests must be approved by you."
                  : "This item type currently supports view-only sharing."}
              </p>
            </div>

            <Button
              onClick={handleShare}
              disabled={!selectedPersonId || createGrantMutation.isPending || availableContacts.length === 0}
              className="w-full"
              data-testid="button-share-submit"
            >
              <Users className="w-4 h-4 mr-2" />
              {createGrantMutation.isPending ? 'Sharing...' : 'Share'}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Current Shares</h3>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading shares...</div>
            ) : combinedShares.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                Not shared with anyone yet
              </div>
            ) : (
              <div className="space-y-2">
                {combinedShares.map((share) => (
                  <div
                    key={share.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg gap-3"
                    data-testid={`share-item-${share.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-2" data-testid={`share-user-name-${share.id}`}>
                        {share.recipientName}
                        {share.isLegacy && <Badge variant="outline" className="text-[10px] h-4 px-1">Legacy</Badge>}
                      </div>
                      <div className="text-sm text-muted-foreground truncate" data-testid={`share-user-email-${share.id}`}>
                        {share.recipientEmail}
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
                      {share.isLegacy ? (
                        <Badge variant={share.permission === 'edit_delete' && itemType !== "task" ? 'destructive' : share.permission === 'edit' || itemType === "task" ? 'default' : 'secondary'} data-testid={`share-permission-${share.id}`}>
                          {share.permission === 'edit_delete' && itemType !== "task" ? 'Can Edit & Delete' : share.permission === 'edit' || itemType === "task" ? 'Can Edit' : 'View Only'}
                        </Badge>
                      ) : supportsEditing ? (
                        <Select
                          value={itemType === "task" && share.permission === "edit_delete" ? "edit" : share.permission}
                          onValueChange={(val) => handleUpdatePermission(share.id, val, share.isLegacy)}
                          disabled={updateGrantMutation.isPending}
                        >
                          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid={`select-update-permission-${share.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="view">View</SelectItem>
                            <SelectItem value="edit">View and edit</SelectItem>
                            {itemType !== "task" && <SelectItem value="edit_delete">View/edit/request deletion</SelectItem>}
                          </SelectContent>
                        </Select>
                      ) : share.permission === "view" ? (
                        <Badge variant="secondary">View Only</Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdatePermission(share.id, "view", false)}
                          disabled={updateGrantMutation.isPending}
                        >
                          Restrict to view only
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevoke(share.id, share.isLegacy)}
                        disabled={revokeGrantMutation.isPending || unshareGoalMutation.isPending || unshareTaskMutation.isPending || unshareCalendarEventMutation.isPending || unshareNoteMutation.isPending}
                        data-testid={`button-unshare-${share.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="button-close-share-dialog">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
